import { useEffect, useState } from 'react';
import type { NextPage } from 'next';
import { signIn } from 'next-auth/react';
import { OmitStrict } from 'type-zoo/types';

import { Container, useForm } from '@dansk-metal/ui';

import { ContentDrawerProps } from '@web/components/content-drawer/content-drawer';
import { EventCancellationDrawer } from '@web/components/event-cancellation-drawer';
import { EventType } from '@web/components/event-card/types';
import { EventDetailCard } from '@web/components/event-detail-card/event-detail-card';
import { RegistrationDrawer } from '@web/components/event-registration-drawer';
import { buildRegistrationInputs, RegistrationFormData } from '@web/components/event-registration-drawer/event-registration-form';
import HeroTitle from '@web/components/hero-block/hero-title/hero-title';
import { Richtext } from '@web/components/richtext/richtext';

import { useGlobalContext } from '@web/layouts/page/context';
import TemplateLayout from '@web/layouts/template/template';

import { useAuthenticatedCRMRequestConfig } from '@web/services/crm-api/hooks';
import { deleteMemberEventRegistration, EventDto, EventRegistrationInput, postMemberEventRegistration, useGetMemberById } from '@web/services/crm-api/rest';
import { trackEventSync } from '@web/services/tracking';
import { Action, Context } from '@web/services/tracking/config';
import { HeroAlignment } from '@web/services/umbraco/types/basic/Alignment';
import { BackgroundColor } from '@web/services/umbraco/types/basic/Color';
import { UmbracoPage } from '@web/services/umbraco/types/documents/UmbracoDocument';

import { useGetEventWrapper } from '@web/templates/event-page/hooks/use-get-event-wrapper';

import { useEventStatus } from '@web/hooks/use-event-status';

import styles from './event-page.module.scss';

export const eventPageContentAlias = 'eventPage' as const;

export type EventPageData = UmbracoPage<
	typeof eventPageContentAlias,
	{
		event: EventDto;
	},
	{
		userEventsPageUrl: string | undefined;
		userContactInfoPageUrl: string | undefined;
	}
>;

const validStatusActions: EventType[] = [
	EventType.Default,
	EventType.RegisteredUser,
	EventType.InvitedUser,
	EventType.OpenForAll,
	EventType.NotLoggedInOpenForAll,
	EventType.NotLoggedInInvitationRequired,
];

const EventPageTemplate: NextPage<{ pageData: EventPageData }> = ({ pageData }) => {
	const [visible, setVisible] = useState(false);
	const openDrawer = () => setVisible(true);
	const closeDrawer = () => setVisible(false);
	const { preview } = useGlobalContext();
	const [event, setEvent] = useState(pageData.properties.event);
	const status = useEventStatus(event);
	const eventId = event?.eventId || '';
	const registerForm = useForm();
	const [shouldShowConfirmation, setShouldShowConfirmation] = useState(false);
	const [shouldShowCancelConfirmation, setShouldShowCancelConfirmation] = useState(false);
	const [isSubmittingRegistration, setIsSubmittingRegistration] = useState(false);

	const drawerProps: OmitStrict<ContentDrawerProps, 'children'> = {
		open: visible,
		onClose: closeDrawer,
	};

	const { memberId, requestConfig } = useAuthenticatedCRMRequestConfig();
	const { data: member, isLoading: isMemberLoading } = useGetMemberById(memberId, { request: requestConfig });

	const {
		data: eventData,
		isFetched,
		isLoading: isEventLoading,
		refetch: refetchEvent,
	} = useGetEventWrapper(eventId);

	const isLoading = (isMemberLoading && !!memberId) || isEventLoading;

	useEffect(() => {
		if (isFetched && eventData) {
			setEvent(eventData);
		}
	}, [eventData, isFetched]);

	useEffect(() => {
		if (!visible) {
			setShouldShowConfirmation(false);
			setShouldShowCancelConfirmation(false);
		}
	}, [visible]);

	const getTitle = () => {
		if (status === EventType.NotLoggedInOpenForAll || status === EventType.NotLoggedInInvitationRequired) {
			return 'Log ind og tilmeld';
		}

		return status === EventType.RegisteredUser ? 'Afmeld' : 'Tilmeld';
	};

	const handleRegistration = () => {
		if ((status === EventType.NotLoggedInOpenForAll || status === EventType.NotLoggedInInvitationRequired) && !preview) {
			signIn('signaturgruppen');
			trackEventSync({ event: [Context.MemberArea, Action.Signin] });
		} else {
			openDrawer();
		}
	};

	async function onCancelEvent() {
		try {
			await deleteMemberEventRegistration(memberId, eventId, { ...requestConfig });
			refetchEvent();
			setShouldShowCancelConfirmation(true);
			setShouldShowConfirmation(false);
		} catch (error) {
			console.error(error);
		}
	}

	async function onRegisterEvent(data: RegistrationFormData) {
		// Transform form data to registration inputs
		const registrationInputs: EventRegistrationInput[] = buildRegistrationInputs(
			event?.registrationForm?.sections || [],
			data,
		);
		setIsSubmittingRegistration(true);

		try {
			await postMemberEventRegistration(memberId, eventId, {
				registrationInputs,
			}, { ...requestConfig });
			refetchEvent();
			setShouldShowConfirmation(true);
			setShouldShowCancelConfirmation(false);
			setIsSubmittingRegistration(false);
			registerForm.reset();
		} catch (error) {
			console.error(error);
		}
	}

	const getDrawerContent = () => {
		if ((status !== EventType.RegisteredUser && !shouldShowCancelConfirmation) ||
			(status === EventType.RegisteredUser && shouldShowConfirmation)) {
			return (
				<RegistrationDrawer
					showConfirmation={shouldShowConfirmation}
					drawer={drawerProps}
					event={event}
					form={{
						form: registerForm,
						onSubmit: onRegisterEvent,
						formSections: event.registrationForm?.sections,
						member,
						userContactInfoPageUrl: pageData.userContactInfoPageUrl || '/',
						isSubmitting: isSubmittingRegistration,
					}}
					userEventsUrl={pageData.userEventsPageUrl || '/'}
				/>
			);
		}

		if ((status === EventType.RegisteredUser && !shouldShowConfirmation) ||
			(status !== EventType.RegisteredUser && shouldShowCancelConfirmation)) {
			return (
				<EventCancellationDrawer
					shouldShowConfirmation={shouldShowCancelConfirmation}
					event={event}
					drawer={drawerProps}
					onSubmit={onCancelEvent}
				/>
			);
		}
	};

	return (
		<TemplateLayout>
			{event.title && (
				<HeroTitle
					title={event.title}
					subtitle={event.intro || ''}
					alignment={HeroAlignment.Left}
					background={BackgroundColor.White}
				/>
			)}

			<Container className={styles.container}>
				<EventDetailCard
					status={status}
					event={event}
					button={{
						title: getTitle(),
						onClick: handleRegistration,
						disabled: isFetched && !event.registrationForm && !validStatusActions.includes(status),
					}}
					isLoading={isLoading}
				/>
				{event.body && <Richtext className={styles.content} content={event.body} />}
			</Container>

			{getDrawerContent()}

		</TemplateLayout>
	);
};

export default EventPageTemplate;
